import React, { useRef } from 'react'
import '@ica-azure/ica-elements/IcaInputNumberStepper'

type IcaInputNumberStepperProps = React.JSX.IntrinsicElements['tgdb-ica-input-number-stepper']

type IcaInputNumberStepperEventHandlers = {
  onFocus?: (e: Event) => void
  onBlur?: (e: Event) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onInput?: (e: Event) => void
  onKeydown?: (e: Event) => void
}

type IcaInputNumberStepperComponentProps = IcaInputNumberStepperProps & IcaInputNumberStepperEventHandlers

const VALUE_CHANGE_EVENTS = ['input', 'change'] as const
const FOCUS_EVENTS = ['focus'] as const
const BLUR_EVENTS = ['blur'] as const
const KEYDOWN_EVENTS = ['keydown'] as const

export function IcaInputNumberStepper({
  min,
  max,
  value,
  step,
  onFocus,
  onBlur,
  onChange,
  onInput,
  onKeydown,
  ...rest
}: IcaInputNumberStepperComponentProps) {
  const elementRef = useRef<HTMLElement>(null)

  React.useEffect(() => {
    const host = elementRef.current
    if (!host) return

    const innerInput = (host as any).shadowRoot?.querySelector('input') || host.querySelector('input')
    const targets = [host, innerInput].filter(Boolean) as EventTarget[]

    const handleValueChange = (e: Event) => {
      const currentValue = (host as any)?.value ?? (e.target as any)?.value ?? (innerInput as any)?.value
      
      if (onChange) {
        const syntheticEvent = { target: { value: currentValue } } as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
      if (onInput) onInput(e)
    }

    const handleBlur = (e: Event) => onBlur?.(e)
    const handleFocus = (e: Event) => onFocus?.(e)
    const handleKeydown = (e: Event) => onKeydown?.(e)

    targets.forEach(target => {
      VALUE_CHANGE_EVENTS.forEach(event => target.addEventListener(event, handleValueChange))
      FOCUS_EVENTS.forEach(event => target.addEventListener(event, handleFocus))
      BLUR_EVENTS.forEach(event => target.addEventListener(event, handleBlur))
      KEYDOWN_EVENTS.forEach(event => target.addEventListener(event, handleKeydown))
    })

    return () => {
      targets.forEach(target => {
        VALUE_CHANGE_EVENTS.forEach(event => target.removeEventListener(event, handleValueChange))
        FOCUS_EVENTS.forEach(event => target.removeEventListener(event, handleFocus))
        BLUR_EVENTS.forEach(event => target.removeEventListener(event, handleBlur))
        KEYDOWN_EVENTS.forEach(event => target.removeEventListener(event, handleKeydown))
      })
    }
  }, [onFocus, onBlur, onChange, onInput, onKeydown])

  return (
    <tgdb-ica-input-number-stepper
      ref={elementRef}
      min={min}
      max={max}
      value={value}
      step={step}
      {...rest}
    />
  )
}

export default IcaInputNumberStepper